import Stripe from "stripe";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";

const STRIPE_CONFIG_COLLECTION = "stripeConfig";

export const getStripeSecretKey = async (storeId?: string): Promise<string | null> => {
  try {
    // For RMS-POS-SUPER, always use global config
    // For RMS-POS, can use storeId-specific config or fallback to global
    const configDocId = storeId || "global";
    const configDocRef = doc(db, STRIPE_CONFIG_COLLECTION, configDocId);
    const configDoc = await getDoc(configDocRef);
    
    if (configDoc.exists()) {
      const data = configDoc.data();
      return data.secretKey || null;
    }
    
    // Fallback: try global config if storeId was provided
    if (storeId && storeId !== "global") {
      const globalConfigRef = doc(db, STRIPE_CONFIG_COLLECTION, "global");
      const globalConfigDoc = await getDoc(globalConfigRef);
      if (globalConfigDoc.exists()) {
        const data = globalConfigDoc.data();
        return data.secretKey || null;
      }
    }
    
    return process.env.REACT_APP_STRIPE_SECRET_KEY || null;
  } catch (error) {
    console.error("Error getting Stripe secret key:", error);
    return process.env.REACT_APP_STRIPE_SECRET_KEY || null;
  }
};

export const getStripeClient = async (storeId?: string): Promise<Stripe | null> => {
  const secretKey = await getStripeSecretKey(storeId);
  
  if (!secretKey) {
    console.warn("⚠️ Stripe secret key not found. Stripe integration will not work.");
    return null;
  }

  try {
    return new Stripe(secretKey, {
      apiVersion: "2025-10-29.clover",
    });
  } catch (error) {
    console.error("Error initializing Stripe client:", error);
    return null;
  }
};

export const getOrCreateCustomer = async (
  storeId: string,
  storeEmail: string,
  storeName: string
): Promise<string | null> => {
  const stripeClient = await getStripeClient(storeId);
  if (!stripeClient) {
    throw new Error("Stripe is not initialized");
  }

  const storeRef = doc(db, "stores", storeId);
  const storeDoc = await getDoc(storeRef);
  
  if (storeDoc.exists()) {
    const storeData = storeDoc.data();
    if (storeData.stripeCustomerId) {
      return storeData.stripeCustomerId;
    }
  }
  
  const customer = await stripeClient.customers.create({
    email: storeEmail,
    name: storeName,
    metadata: {
      storeId: storeId,
    },
  });

  await updateDoc(storeRef, {
    stripeCustomerId: customer.id,
  });

  return customer.id;
};

export const createOrGetProduct = async (
  storeId: string,
  planId: string,
  planName: string,
  price: number
): Promise<{ productId: string; priceId: string } | null> => {
  const stripeClient = await getStripeClient(storeId);
  if (!stripeClient) {
    throw new Error("Stripe is not initialized");
  }
  
  const product = await stripeClient.products.create({
    name: planName,
    metadata: {
      planId: planId,
    },
  });

  const priceObj = await stripeClient.prices.create({
    product: product.id,
    unit_amount: price * 100,
    currency: "usd",
    recurring: {
      interval: "month",
    },
    metadata: {
      planId: planId,
    },
  });

  return {
    productId: product.id,
    priceId: priceObj.id,
  };
};

export const createCheckoutSession = async (
  customerId: string,
  priceId: string,
  storeId: string,
  successUrl: string,
  cancelUrl: string
): Promise<string | null> => {
  const stripeClient = await getStripeClient(storeId);
  if (!stripeClient) {
    throw new Error("Stripe is not initialized");
  }

  const session = await stripeClient.checkout.sessions.create({
    customer: customerId,
    payment_method_types: ["card"],
    line_items: [
      {
        price: priceId,
        quantity: 1,
      },
    ],
    mode: "subscription",
    success_url: successUrl,
    cancel_url: cancelUrl,
    metadata: {
      storeId: storeId,
    },
  });

  return session.url;
};

export const createBillingPortalSession = async (
  storeId: string,
  customerId: string,
  returnUrl: string
): Promise<string | null> => {
  const stripeClient = await getStripeClient(storeId);
  if (!stripeClient) {
    throw new Error("Stripe is not initialized");
  }

  const session = await stripeClient.billingPortal.sessions.create({
    customer: customerId,
    return_url: returnUrl,
  });

  return session.url;
};

